import { computed, signal, Component, inject } from '@angular/core'
import { PageHeaderComponent, PageHeaderMenuItem } from '../page-header/page-header.component'
import { PageContentComponent } from '../page-content/page-content.component'
import { FilterService } from 'src/app/shared/services/filter/filter.service'
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  from,
  map,
  NEVER,
  switchMap,
  tap,
} from 'rxjs'
import { ActivatedRoute, Router } from '@angular/router'
import { Location } from '@angular/common'
import { ReferenceDataSource } from '../reference-data/base-reference.service'
import { ActiveFilterListComponent } from '../table/filters/active-filter-list/active-filter-list.component'
import { PageTabsComponent, Tab } from '../page-tabs/page-tabs.component'
import { MatMenuModule } from '@angular/material/menu'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { ColDef, GetRowIdFunc } from 'ag-grid-community'
import { EntityFilterModalComponent } from '../modals/filters/entity-filter-modal.component'
import { ServicePointFilterModalComponent } from '../modals/filters/service-point-filter-modal.component'
import { DateCellComponent } from '../table/cells/date-cell.component'
import { ServicePointCellComponent } from '../table/cells/service_point_cell.component'
import { TableHeaderComponent } from '../table/headers/table-header.component'
import { TabAware } from 'src/app/shared/pipes/forTab'
import { DataTableComponent } from '../table/data-table.component'
import { ServicePointManagementService } from './service-point-management.service'
import { TableSortService } from '../table/services/table-sort.service'
import { TablePaginationComponent } from '../table/table-pagination/table-pagination.component'
import { ActionCellProps } from '../table/cells/action-cell.component'
import { ActionCellComponent } from '../table/cells/action-cell.component'
import { FlagFilterModalComponent } from '../modals/filters/flag-filter-modal'
import { CreateServicePointModalComponent } from '../modals/create-service-point-modal/create-service-point-modal.component'
import { FlagCellComponent } from '../table/cells/flag-cell.component'
import { CreateServicePointAttributesModalComponent } from '../modals/create-service-point-attributes-modal/create-service-point-attributes-modal.component'
import { CreateServicePointFlagModalComponent } from '../modals/create-service-point-flag-modal/create-service-point-flag-modal.component'
import { DeleteServicePointAttributesModalComponent } from '../modals/delete-service-point-attributes-modal/delete-service-point-attributes-modal.component'
import { DeleteServicePointFlagModalComponent } from '../modals/delete-service-point-flag-modal/delete-service-point-flag-modal.component'
import { ExportServicePointAttributesModalComponent } from '../modals/export-service-point-attributes-modal/export-service-point-attributes-modal.component'
import { ExportServicePointFlagsModalComponent } from '../modals/export-service-point-flags-modal/export-service-point-flags-modal.component'
import { ServicePointGroupModal } from '../service-point-groups/service-point-group-modal.component'
import { DateRangeFilterModalComponent } from '../modals/filters/date-range-filter-modal.component'
import { dateOnlyFormat, formatAsUTC } from 'src/app/shared/utilities/date'
import { ServicePointAttribute, ServicePointEffectiveDatedAttribute } from './model'
import { FlagFilter } from 'src/app/shared/services/filter/flag-filter'
import { EntityFilter } from 'src/app/shared/services/filter/entity-filter'
import { DateRangeFilter } from 'src/app/shared/services/filter/date-range-filter'
import { ServicePointGroupFilter } from 'src/app/shared/services/filter/service-point-group-filter'
import { ServicePointSearchFilter } from 'src/app/shared/services/filter/service-point-search-filter'
import { ServicePointFilter } from 'src/app/shared/services/filter/service-point-filter'
import { StringFilter } from 'src/app/shared/services/filter/string-filter'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { ToastService } from '../toasts/toasts.service'
import openModal from '../../shared/services/openModal'
import { Writable } from 'type-fest'
import { ServicePointGroupUpsertRequest } from '../service-point-groups/service-point-group.types'
import { ServicePointGroupService } from '../service-point-groups/service-point-group.service'
import { withCell } from '../table/cells/with-cell'
import { EntityCellComponent } from '../table/cells/entity-cell.component'
import { LinkCellComponent } from '../table/cells/link-cell.component'
import { isEqual } from 'lodash'

const tabKeys = ['attributes', 'flags'] as const

type TabKey = typeof tabKeys[number]

type ColumnsByTabKey = Readonly<Record<TabKey, ColDef[]>>

type EntitySpec = Readonly<{ name: string, display: string, resource: ReferenceDataSource, extras: ColDef }>

// just in case ColDefs identity is something the grid optimizes for just create columns for each tab up front
// complexity is pretty much the same as creating them on the fly, and
// removing 'showOn' suppresses console warnings from ag-grid
const mapColumns = (
  tabAwareColumns: readonly TabAware<ColDef, TabKey>[],
): ColumnsByTabKey => {
  const result: Partial<Writable<ColumnsByTabKey>> = {}
  tabKeys.forEach(key => {
    result[key] = tabAwareColumns.filter(c => c.showOn.includes(key))
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(({ showOn, ...rest }): ColDef => rest)
  })
  return result as ColumnsByTabKey
}

const colDef = (field: string, properties: Partial<ColDef>): ColDef => ({
  field,
  filter: true,
  sortable: true,
  editable: false,
  minWidth: 100,
  width: 150,
  ...properties,
})

@Component({
  selector: 'app-service-point-management',
  imports: [
    MatMenuModule,
    PageHeaderComponent,
    PageContentComponent,
    ActiveFilterListComponent,
    DataTableComponent,
    TablePaginationComponent,
    PageTabsComponent,
  ],
  providers: [
    TableSortService,
  ],
  template: `
    <div app-page-header header="Service Point Management" [menuItems]="activePageActions()"></div>
    <app-page-content [nogap]="true">
      <app-active-filter-list
        [filterGroup]="constructor.name"
        [filterName]="activeFilters()"
      />
      <app-page-tabs
        [tabs]="tabs"
        [selectedTab]="selectedTab()"
        (selectTab)="$event && selectedTab.set($event)"
      />
      <app-data-table
        [columnDefs]="columnMap[selectedTab()]"
        [getRowId]="getRowId"
        [loading]="loading()"
        [data]="data()"
      />
      <app-table-pagination
        [pageSize]="1000"
        [pageNumber]="pageNumber()"
        [totalResults]="dataTotal()"
        (pageNumberChange)="selectedTab() === 'flags' ? flagsPageNumber.set($event) : attributesPageNumber.set($event) "
      />
    </app-page-content>
  `,
})
export class ServicePointManagementComponent {
  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly location = inject(Location)
  private readonly filterService = inject(FilterService)
  private readonly sortService = inject(TableSortService)
  private readonly servicePointManagementService = inject(ServicePointManagementService)
  private readonly modalService = inject(NgbModal)
  private readonly toastService = inject(ToastService)
  private readonly servicePointGroupsService = inject(ServicePointGroupService)

  protected readonly tabs: readonly Tab<TabKey>[] = [
    { value: 'attributes', display: 'Overrides' },
    { value: 'flags', display: 'Flags' },
  ]

  protected readonly flags = [
    {
      name: 'mdma_subtract_from_adjusted_system_load_flag',
      display: 'MDMA Subtract From Adjusted System Load',
    },
    {
      name: 'mdma_add_to_adjusted_system_load_flag',
      display: 'MDMA Add To Adjusted System Load',
    },
    {
      name: 'mdma_aggregation_exclusion_flag',
      display: 'MDMA Aggregation Exclusion',
    },
    {
      name: 'mdma_weather_sensitive_override_flag',
      display: 'MDMA Weather Sensitive Override',
    },
    {
      name: 'tbill_subtract_from_adjusted_system_load_flag',
      display: 'TBill Subtract From Adjusted System Load',
    },
    {
      name: 'tbill_add_to_adjusted_system_load_flag',
      display: 'TBill Add To Adjusted System Load',
    },
    {
      name: 'tbill_aggregation_exclusion_flag',
      display: 'TBill Aggregation Exclusion',
    },
    {
      name: 'tbill_weather_sensitive_flag',
      display: 'TBill Weather Sensitive',
    },
    {
      name: 'bmg_flag',
      display: 'BMG',
    },
  ]

  private readonly entities: readonly EntitySpec[] = [
    {
      name: 'ldc_code',
      display: 'LDC',
      resource: 'localDistributionCompanies',
      extras: withCell<EntityCellComponent<ServicePointAttribute, 'ldc_code'>>(
        EntityCellComponent,
        {
          value: (d) => d.ldc_code,
        },
      )({}),
    },
    {
      name: 'eldc_code',
      display: 'ELDC',
      resource: 'localDistributionCompanies',
      extras: withCell<EntityCellComponent<ServicePointAttribute, 'eldc_code'>>(
        EntityCellComponent,
        {
          value: (d) => d.eldc_code,
        },
      )({}),
    },
    {
      name: 'supply_class_code',
      display: 'Supply Class',
      resource: 'supplyClasses',
      extras: withCell<EntityCellComponent<ServicePointAttribute, 'supply_class_code'>>(
        EntityCellComponent,
        {
          value: (d) => d.supply_class_code,
        },
      )({}),
    },
    {
      name: 'profile_class_code',
      display: 'Profile Class',
      resource: 'profileClasses',
      extras: withCell<EntityCellComponent<ServicePointAttribute, 'profile_class_code'>>(
        EntityCellComponent,
        {
          value: (d) => d.profile_class_code,
        },
      )({}),
    },
    {
      name: 'retailer_code',
      display: 'Retailer',
      resource: 'retailers',
      extras: withCell<LinkCellComponent<ServicePointAttribute, 'retailer_code'>>(
        LinkCellComponent,
        {
          isVisible: (d) => !!d.retailer_uid,
          display: (d) => d.retailer_code,
          url: (d) => {
            const id = d.retailer_uid
            return id ? `/data-management/entities/RTLR/${id}` : '#'
          },
        },
      )({}),
    },
    {
      name: 'loss_class_code',
      display: 'Loss Class',
      resource: 'lossClasses',
      extras: withCell<EntityCellComponent<ServicePointAttribute, 'loss_class_code'>>(
        EntityCellComponent,
        {
          value: (d) => d.loss_class_code,
        },
      )({}),
    },
    {
      name: 'location_code',
      display: 'Location',
      resource: 'locations',
      extras: withCell<EntityCellComponent<ServicePointAttribute, 'location_code'>>(
        EntityCellComponent,
        {
          value: (d) => d.location_code,
        },
      )({}),
    },
    {
      name: 'meter_type_code',
      display: 'Meter Type',
      resource: 'meterTypes',
      extras: withCell<EntityCellComponent<ServicePointAttribute, 'meter_type_code'>>(
        EntityCellComponent,
        {
          value: (d) => d.meter_type_code,
        },
      )({}),
    },
    {
      name: 'service_point_source_code',
      display: 'Service Point Source',
      resource: 'servicePointSources',
      extras: withCell<EntityCellComponent<ServicePointAttribute, 'service_point_source_code'>>(
        EntityCellComponent,
        {
          value: (d) => d.service_point_source_code,
        },
      )({}),
    },
  ]

  private readonly filters = [
    this.showOn(['attributes', 'flags'], { name: 'service_point_uid' }),
    this.showOn(['attributes', 'flags'], { name: 'service_point_group_uid' }),
    this.showOn(['attributes', 'flags'], { name: 'service_point_id_search' }),
    this.showOn(['attributes', 'flags'], { name: 'effective_date' }),
    this.showOn(['attributes', 'flags'], { name: 'termination_date' }),
    ...this.entities.map((e) => this.showOn(['attributes'], { name: e.name })),
    ...this.flags.map((e) => this.showOn(['flags'], { name: e.name })),
  ]

  private readonly pageActions: readonly TabAware<PageHeaderMenuItem, TabKey>[] = [
    this.showOn(['attributes', 'flags'], {
      label: 'Create Service Point',
      onClick: () => this.openCreateServicePoint(),
    }),
    this.showOn(['attributes', 'flags'], {
      label: 'Create Service Point Group',
      onClick: () => this.openCreateServicePointGroup(),
    }),
    this.showOn(['attributes'], {
      label: 'Create Overrides',
      onClick: () => this.openCreateAttributes(),
    }),
    this.showOn(['attributes'], {
      label: 'Export Overrides',
      onClick: () => this.openExportAttributes(),
    }),
    this.showOn(['flags'], {
      label: 'Create Flags',
      onClick: () => this.openCreateFlags(),
    }),
    this.showOn(['flags'], {
      label: 'Export Flags',
      onClick: () => this.openExportFlags(),
    }),
  ]

  private tabAwareColumns: readonly TabAware<ColDef, TabKey>[] = [
    this.showOn(['attributes', 'flags'], this.servicePointColumn()),
    this.showOn(
      ['attributes', 'flags'],
      this.dateRangeColumn('effective_date', 'Effective Date'),
    ),
    this.showOn(
      ['attributes', 'flags'],
      this.dateRangeColumn('termination_date', 'Termination Date'),
    ),
    ...this.entities.map((e) =>
      this.showOn(
        ['attributes'],
        this.entityCodeColumn(e.name, e.display, e.extras),
      ),
    ),
    ...this.flags.map((e) =>
      this.showOn(['flags'], this.flagColumn(e.name, e.display)),
    ),
    this.showOn(
      ['attributes'],
      this.actionColumn<ServicePointAttribute>([
        {
          name: 'Edit Overrides',
          onAction: (d: ServicePointAttribute) => this.openEditAttributes(d),
          disabled: (d: ServicePointAttribute) => d.service_point_source_code !== 'ESS',
        },
        {
          name: 'Clone Overrides',
          onAction: (d: ServicePointAttribute) => this.openCloneAttributes(d),
          disabled: (d: ServicePointAttribute) =>
            d.service_point_source_code !== 'ESS',
        },
        {
          name: 'Delete Overrides',
          onAction: (d: ServicePointAttribute) => this.openDeleteAttributes(d),
          disabled: (d: ServicePointAttribute) =>
            d.service_point_source_code !== 'ESS',
          sensitive: true,
        },
      ]),
    ),
    this.showOn(
      ['flags'],
      this.actionColumn<ServicePointEffectiveDatedAttribute>([
        {
          name: 'Edit Flag',
          onAction: (d: ServicePointEffectiveDatedAttribute) =>
            this.openEditFlags(d),
        },
        {
          name: 'Clone Flag',
          onAction: (d: ServicePointEffectiveDatedAttribute) =>
            this.openCloneFlags(d),
        },
        {
          name: 'Delete Flag',
          onAction: (d: ServicePointEffectiveDatedAttribute) =>
            this.openDeleteFlags(d),
          sensitive: true,
        },
      ]),
    ),
  ]

  protected tabAwareExtraSortKeys = [
    this.showOn(['attributes', 'flags'], { name: 'service_point_id' }),
  ]

  protected columnMap: ColumnsByTabKey = mapColumns(this.tabAwareColumns)

  protected getRowId: GetRowIdFunc<ServicePointAttribute | ServicePointEffectiveDatedAttribute> =
    ({ data }) => data.service_point_uid + formatAsUTC(data.effective_date)

  protected selectedTab = signal<TabKey>(this.tabs[0].value)
  protected activeFilters = computed(() =>
    this.filters.filter(f =>
      f.showOn.includes(this.selectedTab())).map(f => f.name,
    ),
  )
  protected activePageActions = computed(() =>
    this.pageActions.filter(pa => pa.showOn.includes(this.selectedTab())),
  )
  // data loading
  protected attributesLoading = signal(false)
  protected flagsLoading = signal(false)
  protected loading = computed(() =>
    this.selectedTab() === 'flags' ? this.flagsLoading() : this.attributesLoading(),
  )
  // pages numbers
  protected attributesPageNumber = signal(1)
  protected flagsPageNumber = signal(1)
  protected pageNumber = computed(() =>
    this.selectedTab() === 'flags' ? this.flagsPageNumber() : this.attributesPageNumber(),
  )
  // data totals
  protected attributesDataTotal = signal(0)
  protected flagsDataTotal = signal(0)
  protected dataTotal = computed(() =>
    this.selectedTab() === 'flags' ? this.flagsDataTotal() : this.attributesDataTotal(),
  )
  // data
  protected attributesData = signal<ServicePointAttribute[]>([])
  protected flagsData = signal<ServicePointEffectiveDatedAttribute[]>([])
  protected data = computed(() =>
    this.selectedTab() === 'flags' ? this.flagsData() : this.attributesData(),
  )
  // refresh trigger
  protected attributesRefresh = signal(0)
  protected flagsRefresh = signal(0)

  constructor() {
    const { dispatch } = this.filterService
    dispatch(FilterService.InitFiltersAction({
      fieldGroup: ServicePointManagementComponent.name,
      fields: {
        service_point_uid: new ServicePointFilter({
          config: {
            allowMultiple: false,
            meta: {
              fieldGroup: ServicePointManagementComponent.name,
              fieldName: 'service_point_uid',
              fieldDisplay: 'Service Point',
            },
          },
        }, dispatch),
        service_point_group_uid: new ServicePointGroupFilter({
          config: {
            allowMultiple: false,
            meta: {
              fieldGroup: ServicePointManagementComponent.name,
              fieldName: 'service_point_group_uid',
              fieldDisplay: 'Service Point',
            },
          },
        }, dispatch),
        service_point_id_search: new ServicePointSearchFilter({
          config: {
            meta: {
              fieldGroup: ServicePointManagementComponent.name,
              fieldName: 'service_point_id_search',
              fieldDisplay: 'Service Point',
            },
          },
        }, dispatch),
        effective_date: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            meta: {
              fieldGroup: ServicePointManagementComponent.name,
              fieldName: 'effective_date',
              fieldDisplay: 'Effective Date',
            },
          },
        }, dispatch),
        termination_date: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            meta: {
              fieldGroup: ServicePointManagementComponent.name,
              fieldName: 'termination_date',
              fieldDisplay: 'Termination Date',
            },
          },
        }, dispatch),
        create_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            meta: {
              fieldGroup: ServicePointManagementComponent.name,
              fieldName: 'create_time',
              fieldDisplay: 'Created On',
            },
          },
        }, dispatch),
        last_update_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            meta: {
              fieldGroup: ServicePointManagementComponent.name,
              fieldName: 'last_update_time',
              fieldDisplay: 'Last Updated',
            },
          },
        }, dispatch),
        ...this.entities.reduce((acc, e) => ({
          ...acc, [e.name]: new EntityFilter({
            config: {
              allowMultiple: true,
              resource: e.resource,
              meta: {
                fieldGroup: ServicePointManagementComponent.name,
                fieldName: e.name,
                fieldDisplay: e.display,
              },
            },
          }, dispatch),
        }), {}),
        ...this.flags.reduce((acc, f) => ({
          ...acc, [f.name]: new FlagFilter({
            config: {
              meta: {
                fieldGroup: ServicePointManagementComponent.name,
                fieldName: f.name,
                fieldDisplay: f.display,
              },
            },
          }, dispatch),
        }), {}),
        tab: new StringFilter({
          config: {
            allowMultiple: false,
            meta: {
              fieldGroup: ServicePointManagementComponent.name,
              fieldName: 'tab',
              fieldDisplay: 'Selected Tab',
            },
          },
        }, dispatch),
      },
    }))

    combineLatest([
      toObservable(this.attributesPageNumber),
      this.filterService
        .asParams(ServicePointManagementComponent.name)
        .pipe(
          tap(() => {
            if (this.attributesPageNumber() !== 1) {
              this.attributesPageNumber.set(1)
            }
          }),
          distinctUntilChanged(isEqual),
        ),
      this.sortService.getOrder().pipe(
        map(so => so.filter(o =>
          this.tabAwareColumns
            .filter(c => c.showOn.includes('attributes'))
            .map(c => c.field).includes(o.name)
          ||
          this.tabAwareExtraSortKeys
            .filter(c => c.showOn.includes('attributes'))
            .map(c => c.name).includes(o.name),
        ),
        ),
        distinctUntilChanged(isEqual),
      ),
      toObservable(this.attributesRefresh),
    ])
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.attributesLoading.set(true)),
        switchMap(([pageNumber, filters, order]) => this.servicePointManagementService
          .getAttributes(
            { pageNumber, pageSize: 1000 },
            filters,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            order as any,
          ),
        ),
        catchError(this.handleAttributesLoadingError('Something went wrong loading overrides!')),
      )
      .subscribe({
        error: this.handleAttributesLoadingError('Something went wrong loading overrides!'),
        next: (res) => {
          this.attributesDataTotal.set(res.total)
          this.attributesData.set(res.results)
          this.attributesLoading.set(false)
        },
      })

    combineLatest([
      toObservable(this.flagsPageNumber),
      this.filterService
        .asParams(ServicePointManagementComponent.name)
        .pipe(
          tap(() => {
            if (this.flagsPageNumber() !== 1) {
              this.flagsPageNumber.set(1)
            }
          }),
          distinctUntilChanged(isEqual),
        ),
      this.sortService.getOrder().pipe(
        map(so => so.filter(o =>
          this.tabAwareColumns
            .filter(c => c.showOn.includes('flags'))
            .map(c => c.field).includes(o.name)
          ||
          this.tabAwareExtraSortKeys
            .filter(c => c.showOn.includes('flags'))
            .map(c => c.name).includes(o.name),
        )),
        distinctUntilChanged(isEqual),
      ),
      toObservable(this.flagsRefresh),
    ])
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(isEqual),
        tap(() => this.flagsLoading.set(true)),
        switchMap(([pageNumber, filters, order]) => this.servicePointManagementService
          .getFlags(
            { pageNumber, pageSize: 1000 },
            filters,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            order as any,
          ),
        ),
        catchError(this.handleFlagLoadingError('Something went wrong loading overrides!')),
      )
      .subscribe({
        error: this.handleFlagLoadingError('Something went wrong loading overrides!'),
        next: (res) => {
          this.flagsDataTotal.set(res.total)
          this.flagsData.set(res.results)
          this.flagsLoading.set(false)
        },
      })

    from(this.filterService.manageQueryParms(
      this.router,
      this.route,
      this.location,
      ServicePointManagementComponent.name,
    ))
      .pipe(
        switchMap(obs$ => obs$),
        takeUntilDestroyed(),
      )
      .subscribe()
  }

  showOn<T>(tabs: TabKey[], value: T): TabAware<T, TabKey> {
    return { ...value, showOn: tabs }
  }

  protected handleAttributesLoadingError(defaultMessage: string){
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (e: any) => {
      this.attributesData.set([])
      this.attributesLoading.set(false)
      this.toastService.show({ type: 'error', message: e.detail ?? defaultMessage })
      return NEVER
    }
  }

  protected handleFlagLoadingError(defaultMessage: string){
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (e: any) => {
      this.flagsData.set([])
      this.flagsLoading.set(false)
      this.toastService.show({ type: 'error', message: e.detail ?? defaultMessage })
      return NEVER
    }
  }

  private servicePointColumn() {
    return colDef('service_point_uid', {
      width: 165,
      headerName: 'Service Point',
      cellRenderer: ServicePointCellComponent,
      headerComponent: TableHeaderComponent,
      headerComponentParams: {
        filterGroup: ServicePointManagementComponent.name,
        sortField: 'service_point_id',
        additionalFields: [
          'service_point_group_uid',
          'service_point_id_search',
        ],
        modal: ServicePointFilterModalComponent,
        modalOptions: { centered: true },
        modalInit: {
          partialField: 'service_point_id_search',
          groupField: 'service_point_group_uid',
        },
      },
    })
  }

  private dateRangeColumn(field: string, headerName: string) {
    return colDef(field, {
      headerName,
      cellRenderer: DateCellComponent,
      cellRendererParams: {
        format: dateOnlyFormat,
        date: (d: ServicePointAttribute) => d[field as keyof ServicePointAttribute],
      },
      headerComponent: TableHeaderComponent,
      headerComponentParams: {
        filterGroup: ServicePointManagementComponent.name,
        modal: DateRangeFilterModalComponent,
        modalOptions: { centered: true },
        modalInit: {},
      },
    })
  }

  private entityCodeColumn(
    field: string,
    headerName: string,
    extras: ColDef = {},
  ) {
    return colDef(field, {
      headerName,
      headerComponent: TableHeaderComponent,
      headerComponentParams: {
        filterGroup: ServicePointManagementComponent.name,
        modal: EntityFilterModalComponent,
        modalOptions: { centered: true },
        modalInit: {},
      },
      ...extras,
    })
  }

  private flagColumn(field: string, headerName: string) {
    return colDef(field, {
      headerName,
      cellRenderer: FlagCellComponent,
      cellRendererParams: {
        flag: (d: ServicePointEffectiveDatedAttribute) => d[field as keyof ServicePointEffectiveDatedAttribute],
      },
      headerComponent: TableHeaderComponent,
      headerComponentParams: {
        filterGroup: ServicePointManagementComponent.name,
        modal: FlagFilterModalComponent,
        modalOptions: { centered: true },
        modalInit: {},
      },
    })
  }

  private actionColumn<T>(
    actions: ActionCellProps<T>['actions'],
  ): ColDef {
    return colDef('actions', {
      width: 75,
      pinned: 'right',
      resizable: false,
      suppressMovable: true,
      filter: false,
      sortable: false,
      cellRenderer: ActionCellComponent<T>,
      cellRendererParams: {
        actions: actions,
      },
      headerComponentParams: {
        filterGroup: ServicePointManagementComponent.name,
      },
    })
  }

  protected openCreateServicePoint() {
    void openModal(
      this.modalService,
      CreateServicePointModalComponent,
      {
        centered: true,
        windowClass: 'wide-modal',
      },
    ).init(
      () => {
        this.attributesRefresh.update(v => v + 1)
        this.flagsRefresh.update(v => v + 1)
      },
    )
  }

  protected async openCreateServicePointGroup() {
    const data = await firstValueFrom(this.filterService.asServicePointGroupWithRefs(ServicePointManagementComponent.name))
    openModal(
      this.modalService,
      ServicePointGroupModal,
      {
        centered: true,
        windowClass: 'wide-modal',
      },
    ).init({
      data,
      onSave: ({ request }) => {
        this.createServicePointGroup(request)
      },
      onCancel: () => this.modalService.dismissAll(),
    })
  }

  protected createServicePointGroup(req: ServicePointGroupUpsertRequest) {
    this.servicePointGroupsService
      .create(req)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'An unknown error occured' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Create successful!' })
          this.modalService.dismissAll()
          this.servicePointGroupsService.refresh()
        },
      })
  }

  protected openCreateAttributes() {
    void openModal(
      this.modalService,
      CreateServicePointAttributesModalComponent,
      {
        centered: true,
        windowClass: 'wide-modal',
      },
    ).init(
      'CREATE',
      undefined,
      () => this.attributesRefresh.update(v => v + 1),
    )
  }

  protected openExportAttributes() {
    const filters = this.filters.filter((f) =>
      f.showOn.includes(this.selectedTab()),
    ).map((f) => f.name)

    void openModal(
      this.modalService,
      ExportServicePointAttributesModalComponent,
      {
        centered: true,
      },
    ).init(ServicePointManagementComponent.name, filters)
  }

  protected openDeleteAttributes(data: ServicePointAttribute) {
    void openModal(
      this.modalService,
      DeleteServicePointAttributesModalComponent,
      {
        centered: true,
        windowClass: 'wide-modal',
      },
    ).init(
      data,
      () => this.attributesRefresh.update(v => v + 1),
    )
  }

  protected openEditAttributes(data: ServicePointAttribute) {
    void openModal(
      this.modalService,
      CreateServicePointAttributesModalComponent,
      {
        centered: true,
        windowClass: 'wide-modal',
      },
    ).init(
      'EDIT',
      data,
      () => this.attributesRefresh.update(v => v + 1),
    )
  }

  protected openCloneAttributes(data: ServicePointAttribute) {
    void openModal(
      this.modalService,
      CreateServicePointAttributesModalComponent,
      {
        centered: true,
        windowClass: 'wide-modal',
      },
    ).init(
      'CLONE',
      data,
      () => this.attributesRefresh.update(v => v + 1),
    )
  }

  openCreateFlags() {
    openModal(
      this.modalService,
      CreateServicePointFlagModalComponent,
      {
        centered: true,
        windowClass: 'wide-modal',
      },
    ).init(
      'CREATE',
      undefined,
      () => this.flagsRefresh.update(v => v + 1),
    )
  }

  protected openExportFlags() {
    const filters = this.filters.filter((f) =>
      f.showOn.includes(this.selectedTab()),
    ).map((f) => f.name)
    openModal(
      this.modalService,
      ExportServicePointFlagsModalComponent,
      {
        centered: true,
      },
    ).init(ServicePointManagementComponent.name, filters)
  }

  protected openDeleteFlags(data: ServicePointEffectiveDatedAttribute) {
    void openModal(
      this.modalService,
      DeleteServicePointFlagModalComponent,
      {
        centered: true,
        windowClass: 'wide-modal',
      },
    ).init(
      data,
      () => this.flagsRefresh.update(v => v + 1),
    )
  }

  protected openEditFlags(data: ServicePointEffectiveDatedAttribute) {
    openModal(
      this.modalService,
      CreateServicePointFlagModalComponent,
      {
        centered: true,
        windowClass: 'wide-modal',
      },
    ).init(
      'EDIT',
      data,
      () => this.flagsRefresh.update(v => v + 1),
    )
  }

  protected openCloneFlags(data: ServicePointEffectiveDatedAttribute) {
    openModal(
      this.modalService,
      CreateServicePointFlagModalComponent,
      {
        centered: true,
        windowClass: 'wide-modal',
      },
    ).init(
      'CLONE',
      data,
      () => this.flagsRefresh.update(v => v + 1),
    )
  }
}

